// src/firebase.js
//
// Wave 3b (part 2, wiring) — Firebase Auth/Firestore/Functions have been
// removed entirely from this dashboard. This file is now a thin shim that
// exposes fetch-based callers for the handful of former Cloud Function
// "callables" that still need a service-role-backed server (see
// src/lib/MIGRATION_MAP.md §4 — these can never become direct supabase-js
// calls: they touch OTHER brands' rows, call the WhatsApp API, and/or the
// GoTrue Admin API). The Express routes these fetch calls hit are being
// built in parallel by another agent — see MIGRATION_MAP.md for the
// expected request/response shape of each. Every other former export of
// this file (`auth`, `db`, `rtDb`, `callInitializeBrandStats` — the last one
// had zero call sites in the UI even before this Wave) is gone, replaced by
// `src/supabaseClient.js` + `src/lib/supabaseAuth.js` +
// `src/lib/confirmationData.js`.

import { supabase } from './supabaseClient';

// Create React App only inlines env vars prefixed with REACT_APP_ at build
// time. Owner must add this to .env.local, e.g.:
//   REACT_APP_CONFIRMATION_API_BASE=https://confirming-app.dafagents.com
const API_BASE = process.env.REACT_APP_CONFIRMATION_API_BASE;

if (!API_BASE) {
  // eslint-disable-next-line no-console
  console.warn(
    '[firebase.js shim] REACT_APP_CONFIRMATION_API_BASE is not set — every ' +
    'call*() helper below will fail until it is added to the dashboard\'s ' +
    '.env.local.'
  );
}

/**
 * Calls one of the Express routes that replace the old Firebase Cloud
 * Function "callables". Route convention: POST {API_BASE}/api/dashboard/<name>.
 *
 * Auth: attaches `Authorization: Bearer <access_token>` from the current
 * GoTrue session when one exists — `sendManualMessage`/`answerCustomerQuestion`
 * need it so the server can resolve "which brand is calling" itself (see
 * MIGRATION_MAP.md §4's security note: the OLD Cloud Functions only checked
 * "is there a session", never "does this session own this resource" — the
 * new routes are expected to fix that). `createNewClient`/
 * `completeShopifyOnboarding` are pre-auth (no session exists yet when
 * they're called) so the header is simply omitted in that case — those two
 * routes must treat a missing Authorization header as the normal case, not
 * an error.
 */
async function callDashboardApi(name, payload) {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData && sessionData.session && sessionData.session.access_token;

  const res = await fetch(`${API_BASE}/api/dashboard/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify(payload || {}),
  });

  let body = null;
  try {
    body = await res.json();
  } catch (e) {
    body = null;
  }

  if (!res.ok) {
    const message = (body && (body.message || body.error)) || `${name} failed (${res.status})`;
    throw new Error(message);
  }

  return body;
}

/** Replaces the old `createNewClient` httpsCallable (SignupPage.js). */
export const callCreateNewClient = (clientData) => callDashboardApi('createNewClient', clientData);

/** Replaces the old `sendManualMessage` httpsCallable (ClientDashboard.js). */
export const callSendManualMessage = (messageData) => callDashboardApi('sendManualMessage', messageData);

/** Replaces the old `answerCustomerQuestion` httpsCallable (ClientDashboard.js). */
export const callAnswerCustomerQuestion = (answerData) => callDashboardApi('answerCustomerQuestion', answerData);

/** Fetches a private WhatsApp image/voice note as a Blob. The API verifies that
 * the media belongs to the signed-in merchant before streaming any bytes. */
export async function fetchChatMedia(mediaId) {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData?.session?.access_token;
  if (!accessToken) throw new Error('يجب تسجيل الدخول لعرض الوسائط');

  const res = await fetch(
    `${API_BASE}/api/dashboard/media/${encodeURIComponent(String(mediaId || ''))}`,
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  if (!res.ok) {
    let message = `تعذر تحميل الوسائط (${res.status})`;
    try {
      const body = await res.json();
      message = body?.message || body?.error || message;
    } catch (_err) {
      // Non-JSON error response: keep the safe fallback above.
    }
    throw new Error(message);
  }
  return res.blob();
}

/** Replaces the old `completeShopifyOnboarding` httpsCallable (ShopifyOnboardingPage.js). */
export const callCompleteShopifyOnboarding = (onboardingData) => callDashboardApi('completeShopifyOnboarding', onboardingData);

// ----------------------------------------------------------------------------
// ⚠️ Extensions beyond the 4 callables named in the task brief. Needed to
// reach "zero real Firestore/Auth/Functions usage" (BostaPage.js /
// WebhookSetupPage.js used to write these fields via a direct Firestore
// updateDoc — see MIGRATION_MAP.md §3, `bosta_api` / `*_shared_secret` rows).
// Both fields are explicit SECRETS excluded from `update_my_brand_settings`'s
// RPC whitelist by design (daf_confirmation_rls.sql) — they need a dedicated
// service-role route, same reasoning as the 4 callables above. These two
// routes are NOT confirmed to exist server-side yet; flagged for the owner /
// the parallel routes-building agent in this task's final report.
// ----------------------------------------------------------------------------

/** Extension — replaces BostaPage.js's direct `updateDoc(clients_api, {bosta_api})`. */
export const callSaveBostaApiKey = (payload) => callDashboardApi('saveBostaApiKey', payload);

/** Extension — replaces WebhookSetupPage.js's direct `updateDoc(clients_api, {..._shared_secret})`. */
export const callSaveWebhookSecrets = (payload) => callDashboardApi('saveWebhookSecrets', payload);

export const callOwnerOrderAction = (data) => callDashboardApi('orderAction', data);

export const callSendOrderNow = (orderId) => callDashboardApi('sendOrderNow', {orderId});
